import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
// import DeleteModal from "components/Common/DeleteModal";
// import UpdateOrderStatusModal from "components/Modals/UpdateOrderStatusModal";
import OrderStatusBadge from "components/Common/OrderStatusBadge";
import { qwikBistroOrderApiService } from "services/qwikBistroOrderApiService";
import DeleteModal from "pages/Calendar/DeleteModal";
import UpdateOrderStatusModal from "components/Modals/UpdateOrderStatusModal";

const AllOrders = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch orders
  const fetchOrders = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await qwikBistroOrderApiService.getAllOrders();
      setOrders(data);
    } catch (error) {
      toast.error("Failed to fetch orders");
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const showDeleteModal = (id) => {
    setSelectedOrderId(id);
    setDeleteModal(true);
  };

  const handleDeleteOrder = async () => {
    setDeletingOrder(true);
    try {
      await qwikBistroOrderApiService.deleteOrder(selectedOrderId);
      toast.success("Order deleted successfully");
      setDeleteModal(false);
      fetchOrders(false); // Refresh without showing loader
    } catch (error) {
      toast.error("Failed to delete order");
      console.error("Error deleting order:", error);
    } finally {
      setDeletingOrder(false);
    }
  };

  const handleUpdateStatus = (order) => {
    setSelectedOrder(order);
    setStatusModalOpen(true);
  };

  const handleStatusUpdateSuccess = () => {
    fetchOrders(false); // Refresh orders after status update
    toast.success("Order status updated successfully");
  };

  // Filter orders based on status and search
  const filteredOrders = useMemo(() => {
    let filtered = orders;

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          (order.qb_order_id && order.qb_order_id.toString().toLowerCase().includes(query)) ||
          (order.phonenumber && order.phonenumber.toString().toLowerCase().includes(query)) ||
          (order.username && order.username.toLowerCase().includes(query)) ||
          (order.address && order.address.toLowerCase().includes(query)) ||
          (order.total_amount && order.total_amount.toString().toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [orders, statusFilter, searchQuery]);

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    return `$${parseFloat(amount).toLocaleString('en-US')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toISOString().slice(0, 10).replace(/-/g, "/");
  };

  const columns = useMemo(
    () => [
      {
        header: "Date & Day",
        accessorKey: "order_date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const orderDate = row.original.order_date;
          if (!orderDate) return "N/A";

          const date = new Date(orderDate);
          const formattedDate = date.toISOString().slice(0, 10).replace(/-/g, "/");
          const day = date.toLocaleDateString("en-US", { weekday: "short" });

          return (
            <div>
              <div className="">{formattedDate}</div>
              <small className="text-muted">{day}</small>
            </div>
          );
        },
      },
      {
        header: "ID",
        accessorKey: "qb_order_id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            style={{
              cursor: "pointer",
              color: "blue",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/update-qwikbistro-order/${row.original.id}`)}
            className=""
          >
            #{row.original.qb_order_id || "-"}
          </div>
        ),
      },
      {
        header: "Customer",
        accessorKey: "username",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const hasUserId = !!row.original.user_id;
          const style = {
            cursor: hasUserId ? "pointer" : "default",
            color: hasUserId ? "blue" : "inherit",
            textDecoration: hasUserId ? "underline" : "none",
          };

          return (
            <div
              style={style}
              onClick={hasUserId ? () => navigate(`/customer-details/${row.original.user_id}`) : undefined}
              className=""
            >
              {row.original.username || "-"}
            </div>
          );
        },
      },
      {
        header: "Phone",
        accessorKey: "phonenumber",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">{row.original.phonenumber || "-"}</div>
        ),
      },
      {
        header: "Order Items",
        accessorKey: "orderproductdata",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const products = row.original.orderproductdata || [];
          return (
            <div>
              {products.map((product, index) => (
                <div key={index} className="mb-1">
                  {/* <small> */}
                  {index + 1}. {product.name} <strong className="text-primary">x{product.quantity}</strong>
                  {/* </small> */}
                </div>
              ))}
              {/* {products.length > 3 && (
                <small className="text-muted">
                  +{products.length - 3} more items
                </small>
              )} */}
            </div>
          );
        },
      },
      {
        header: "Total",
        accessorKey: "total_amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{formatCurrency(row.original.total_amount)}</div>
          </div>
        ),
      },
      {
        header: "Paid",
        accessorKey: "paidamount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {formatCurrency(row.original.paidamount || "0")}
          </div>
        ),
      },
      {
        header: "Paid on date & day",
        accessorKey: "paymentdate",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const paymentDate = row.original.paymentdate;
          if (!paymentDate) return "-";

          const date = new Date(paymentDate);
          const formattedDate = date.toISOString().slice(0, 10).replace(/-/g, "/");
          const day = date.toLocaleDateString("en-US", { weekday: "short" });

          return (
            <div>
              {/* <small> */}
              {formattedDate}
              {/* </small> */}
              <br />
              <small className="text-muted">
                {day}
              </small>
            </div>
          );
        },
      },
      {
        header: "Received By",
        accessorKey: "receivedby",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">{row.original.receivedby || "-"}</div>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <OrderStatusBadge status={row.original.status} />,
      },
      {
        header: "Tips",
        accessorKey: "tips",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className=" text-success">
            {formatCurrency(row.original.tips || "0")}
          </div>
        ),
      },
      {
        header: "Address",
        accessorKey: "address",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
              {row.original.address || "-"}
            </small>
          </div>
        ),
      },
      // {
      //   header: "Actions",
      //   accessorKey: "actions",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }) => (
      //     <div className="d-flex gap-2">
      //       <button
      //         onClick={() => handleUpdateStatus(row.original)}
      //         className="btn btn-warning btn-sm"
      //         title="Update Status"
      //       >
      //         <i className="fas fa-edit"></i>
      //       </button>
      //       <button
      //         className="btn btn-danger btn-sm"
      //         onClick={() => showDeleteModal(row.original.id)}
      //         title="Delete Order"
      //         disabled={deletingOrder}
      //       >
      //         <i className="fas fa-trash"></i>
      //       </button>
      //     </div>
      //   ),
      // },
    ],
    [deletingOrder]
  );

  // Calculate statistics
  const totalOrders = orders.length;
  const totalAmount = orders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
  const pendingOrders = orders.filter(order => order.status === 'pending').length;
  const completedOrders = orders.filter(order => order.status === 'completed').length;
  const cancelledOrders = orders.filter(order => order.status === 'cancelled').length;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Orders" breadcrumbItem="All Orders" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Orders</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{totalOrders}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary rounded-circle">
                        <i className="fas fa-shopping-cart font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Revenue</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{formatCurrency(totalAmount)}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success rounded-circle">
                        <i className="fas fa-dollar-sign font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Pending Orders</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{pendingOrders}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning rounded-circle">
                        <i className="fas fa-clock font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Completed Orders</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{completedOrders}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info rounded-circle">
                        <i className="fas fa-check-circle font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters and Actions */}
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">


                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search orders, customers, phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: "250px" }}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  {/* <label className="form-label mb-0">Status:</label> */}
                  <select
                    className="form-select"
                    style={{ width: "auto" }}
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>


                {(statusFilter || searchQuery) && (
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setStatusFilter("");
                      setSearchQuery("");
                    }}
                  >
                    <i className="fas fa-times me-1"></i> Clear Filters
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                {/* <button
                  onClick={() => navigate("/all-invoice/page")}
                  className="btn btn-primary"
                >
                  <i className="fas fa-file-invoice me-1"></i> Invoices
                </button> */}

                <button
                  onClick={() => navigate("/create-qwikbistro-order")}
                  className="btn btn-success"
                >
                  <i className="fas fa-plus me-1"></i> Create New Order
                </button>

                <button
                  className="btn btn-light"
                  onClick={() => fetchOrders(false)}
                  disabled={refreshing}
                  title="Refresh Orders"
                >
                  <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* <div className="mb-3">
                  <h5 className="card-title mb-0">
                    Showing {filteredOrders.length} of {totalOrders} orders
                  </h5>
                </div> */}

                <TableContainer
                  columns={columns}
                  data={filteredOrders}
                  isGlobalFilter={false}
                  isPagination={true}
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  isCustomPageSize={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteOrder}
          loader={deletingOrder}
          onCloseClick={() => setDeleteModal(false)}
          title="Delete Order"
          message="Are you sure you want to delete this order? This action cannot be undone."
        />

        {selectedOrder && (
          <UpdateOrderStatusModal
            isOpen={statusModalOpen}
            toggle={() => setStatusModalOpen(false)}
            order={selectedOrder}
            onUpdateSuccess={handleStatusUpdateSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default AllOrders;